/**
 * Client-side group photo upload (direct to backend).
 * Use for Volunteers Group Photos tab to avoid Next.js body size limits.
 */

import { GROUP_PHOTOS } from '@/shared/constants/backend-api'
import type { GroupPhoto } from '@/features/group-photos/api/group-photos.api'
import type { ApiResponse } from '@/shared/types/backend-response.type'
import { authFetchJson, authFetch, getAccessToken } from '@/shared/lib/auth-fetch'

const UNSUPPORTED_VIDEO_EXT = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
const UNSUPPORTED_VIDEO_MIME = [
  'video/x-msvideo',
  'video/x-matroska',
  'video/x-flv',
  'video/x-ms-wmv',
  'video/3gpp',
]

export async function uploadMultipleGroupPhotosDirect(
  groupId: string,
  files: File[],
  description?: string,
  tags?: string[]
): Promise<ApiResponse<GroupPhoto[]>> {
  try {
    if (files.length === 0) {
      return { success: false, message: 'At least one file is required', status: 400 }
    }
    if (files.length > 30) {
      return { success: false, message: 'Maximum 30 files can be uploaded at once', status: 400 }
    }

    for (const file of files) {
      const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? ''
      if (UNSUPPORTED_VIDEO_EXT.includes(ext)) {
        return {
          success: false,
          message: `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`,
          status: 400,
        }
      }
      if (UNSUPPORTED_VIDEO_MIME.includes(file.type.toLowerCase())) {
        return {
          success: false,
          message: `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`,
          status: 400,
        }
      }
    }

    const token = getAccessToken()
    if (!token) {
      return { success: false, message: 'Not authenticated', status: 401 }
    }

    const formData = new FormData()
    files.forEach((f) => formData.append('photos', f))
    formData.append('groupId', groupId)
    if (description) formData.append('description', description)
    if (tags?.length) formData.append('tags', JSON.stringify(tags))

    const res = await authFetch(GROUP_PHOTOS.uploadMultiple, {
      method: 'POST',
      body: formData,
      headers: { Authorization: `Bearer ${token}` },
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      const message =
        res.status === 413
          ? 'Request too large. Try fewer or smaller files.'
          : err?.message || `HTTP error! status: ${res.status}`
      return { success: false, message, status: res.status }
    }

    const data = await res.json()
    const photos = data?.data ?? (Array.isArray(data) ? data : [])
    const errors = Array.isArray(data?.errors) ? data.errors : undefined
    const skippedDuplicates = Array.isArray(data?.skippedDuplicates) ? data.skippedDuplicates : undefined
    return {
      success: true,
      data: photos,
      ...(errors?.length && { errors }),
      ...(skippedDuplicates?.length && { skippedDuplicates }),
    }
  } catch (e) {
    console.error('Error uploading group photos (direct):', e)
    return {
      success: false,
      message: e instanceof Error ? e.message : 'Unknown error occurred',
      status: 500,
    }
  }
}
